// Surveys sub-tab for the In-Room QR admin panel — everything inlined
// because the visual-edits Babel plugin stack-overflows on sub-components
// that `.map()` their own props.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Star, MessageSquare, CheckCircle2 } from 'lucide-react';
import { apiCall } from './_shared';

const InRoomSurveysTab = () => {
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [statsJson, surveysJson] = await Promise.all([
        apiCall(`/api/admin/inroom/surveys/stats?days=${days}`),
        apiCall(`/api/admin/inroom/surveys?limit=200`),
      ]);
      setStats(statsJson);
      setSurveys(surveysJson.items || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (surveyId) => {
    const note = window.prompt('What did reception do about this? (e.g. "Moved guest to sea-view room, complimentary breakfast")');
    if (!note || note.trim().length < 2) return;
    setResolvingId(surveyId);
    try {
      await apiCall(`/api/admin/inroom/surveys/${surveyId}/resolve`, {
        method: 'POST', body: JSON.stringify({ action_taken: note.trim() }),
      });
      await load();
    } catch (e) { alert(e.message); }
    finally { setResolvingId(null); }
  };

  const cats = stats?.by_category || {};
  const rooms = stats?.by_room || [];
  // Prefer live surveys[] for the "recent notes" panel so we can render Resolve
  // buttons; fall back to stats.recent_comments if the list endpoint failed.
  const commentsSource = surveys.filter((s) => s.comment).slice(0, 8);
  const openLowRatings = surveys.filter((s) => s.low_rating && !s.resolved_at).length;

  const windowBtns = [30, 90, 365].map((d) => (
    <button
      key={d}
      onClick={() => setDays(d)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${days === d ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-700'}`}
      data-testid={`survey-window-${d}`}
    >
      {d === 365 ? 'Last year' : `Last ${d}d`}
    </button>
  ));

  const catRows = Object.entries(cats).map(([k, v]) => (
    <div key={k} className="flex items-center gap-3">
      <span className="text-xs font-semibold text-slate-600 w-28 capitalize">{k}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500" style={{ width: `${(v.avg / 5) * 100}%` }} />
      </div>
      <span className="text-sm font-bold text-slate-900 w-14 text-right">
        {v.avg.toFixed(2)}<span className="text-xs text-slate-400">/5</span>
      </span>
      <span className="text-xs text-slate-400 w-10 text-right">n={v.count}</span>
    </div>
  ));

  const roomRows = rooms.map((r, i) => (
    <tr key={r.room_id || i} className="border-b last:border-b-0" data-testid={`survey-room-${r.room_id}`}>
      <td className="py-2 pr-3 text-sm text-slate-900 truncate max-w-[220px]">{r.room_name || `Room ${r.room_id}`}</td>
      <td className="py-2 pr-3 text-sm text-right font-bold text-orange-600">{r.avg != null ? r.avg.toFixed(2) : '—'}</td>
      <td className="py-2 pr-3 text-xs text-right text-slate-500">{r.count}</td>
    </tr>
  ));

  const commentRows = commentsSource.map((c, i) => {
    const low = !!c.low_rating;
    const resolved = !!c.resolved_at;
    return (
      <li
        key={c.id || i}
        className={`text-sm border-l-2 pl-3 py-1 ${
          resolved ? 'border-slate-300 opacity-70' :
          low ? 'border-red-500 bg-red-50 rounded' : 'border-orange-400'
        }`}
        data-testid={`survey-comment-${i}`}
      >
        {low && !resolved && (
          <p className="text-[10px] uppercase tracking-widest font-bold text-red-600 mb-0.5">⚠ Needs follow-up</p>
        )}
        {resolved && (
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-0.5 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Resolved
          </p>
        )}
        <p className={`italic ${low && !resolved ? 'text-red-900' : 'text-slate-700'}`}>"{c.comment}"</p>
        <p className="text-xs text-slate-500 mt-1">
          {c.room_name} · {c.overall}/5 · {c.guest_name || 'anonymous'}
        </p>
        {resolved && (
          <p className="text-xs text-emerald-700 mt-1 bg-emerald-50 rounded px-2 py-1" data-testid={`survey-action-${c.id}`}>
            ✓ {c.action_taken} — <span className="text-emerald-600">{c.resolved_by}</span>
          </p>
        )}
        {low && !resolved && (
          <button
            onClick={() => resolve(c.id)}
            disabled={resolvingId === c.id}
            className="mt-1 text-xs px-2 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`survey-resolve-${c.id}`}
          >
            {resolvingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Resolve
          </button>
        )}
      </li>
    );
  });

  return (
    <div data-testid="inroom-surveys-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">{windowBtns}</div>
        <button onClick={load} disabled={loading} className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>
      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
      {stats && stats.total_responses === 0 ? (
        <div className="text-center py-16 text-slate-400 bg-white rounded-xl border" data-testid="survey-empty">
          <Star className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">No survey responses in the last {days} days yet.</p>
        </div>
      ) : stats ? (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-gradient-to-br from-orange-500 to-rose-600 text-white rounded-2xl p-6 shadow-lg" data-testid="survey-overall">
            <p className="text-xs uppercase tracking-widest opacity-90">Overall score</p>
            <p className="text-5xl font-bold mt-1">{stats.overall_avg != null ? stats.overall_avg.toFixed(2) : '—'}</p>
            <p className="text-xs opacity-80 mt-2">{stats.total_responses} response{stats.total_responses > 1 ? 's' : ''} · {stats.window_days} days</p>
          </div>
          <div className="bg-white rounded-2xl border p-4 md:col-span-2">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">By category</p>
            <div className="space-y-2">{catRows}</div>
          </div>
          <div className="bg-white rounded-2xl border p-4 md:col-span-2">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Per room</p>
            <table className="w-full">
              <thead className="text-xs uppercase text-slate-500">
                <tr className="border-b">
                  <th className="text-left py-2 pr-3">Room</th>
                  <th className="text-right py-2 pr-3">Avg</th>
                  <th className="text-right py-2 pr-3">n</th>
                </tr>
              </thead>
              <tbody>{roomRows}</tbody>
            </table>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3 flex items-center gap-1">
              <MessageSquare className="w-4 h-4" /> Recent notes
            </p>
            {commentsSource.length === 0 ? (
              <p className="text-xs text-slate-400">No written feedback yet.</p>
            ) : (
              <ul className="space-y-3">{commentRows}</ul>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
      )}
    </div>
  );
};

export default InRoomSurveysTab;
